import type { CountriesMap } from "@/lib/sign-up-countries";
import { resolveCountryCode } from "@/lib/sign-up-countries";
import { useSignUpCountries } from "@/features/auth/api/get-countries";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ControllerRenderProps, FieldValues } from "react-hook-form";

import { FormControl, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SignUpCountrySelect } from "./sign-up-country-select";

/** Mirrors the shape we persist for `signUpExtensions` (server validates `line1` + `country` when required). */
export type SignUpAddressValue = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

type AddressPayload = { complete: boolean; value: SignUpAddressValue };

function parseStored(raw: unknown): SignUpAddressValue {
  if (raw == null || raw === "") return {};
  if (typeof raw !== "string") return {};
  const t = raw.trim();
  if (!t) return {};
  try {
    const o = JSON.parse(t) as { value?: SignUpAddressValue };
    return o?.value && typeof o.value === "object" ? { ...o.value } : {};
  } catch {
    return {};
  }
}

function allEmpty(v: SignUpAddressValue): boolean {
  return !Object.values(v).some(s => String(s ?? "").trim());
}

function isComplete(v: SignUpAddressValue): boolean {
  return Boolean(v.line1?.trim() && v.country?.trim());
}

function serialize(required: boolean, v: SignUpAddressValue): string {
  if (!required && allEmpty(v)) return "";
  return JSON.stringify({ complete: isComplete(v), value: v } satisfies AddressPayload);
}

function normalizeCountryInAddress(map: CountriesMap, v: SignUpAddressValue): SignUpAddressValue {
  const r = resolveCountryCode(map, v.country);
  if (r && r !== (v.country ?? "").trim()) return { ...v, country: r };
  return v;
}

const FLUSH_MS = 200;

type Props = {
  field: ControllerRenderProps<FieldValues, string>;
  label: string;
  required: boolean;
  readOnly?: boolean;
};

export const SignUpAddressField = ({ field, label, required, readOnly }: Props) => {
  const { countriesMap, countryOptions, isPending } = useSignUpCountries();
  const map = useMemo(() => countriesMap ?? ({} as CountriesMap), [countriesMap]);

  const lastSerializedRef = useRef<string | undefined>(undefined);
  const draftRef = useRef<SignUpAddressValue>({});
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [draft, setDraft] = useState<SignUpAddressValue>(() => parseStored(field.value));
  draftRef.current = draft;

  const pushToForm = useCallback(
    (normalized: SignUpAddressValue) => {
      if (readOnly) return;
      const s = serialize(required, normalized);
      const incoming = typeof field.value === "string" ? field.value : "";
      if (s === incoming) return;
      lastSerializedRef.current = s;
      field.onChange(s);
    },
    [field, readOnly, required]
  );

  // Parent changed the stored value (reset, use case switch) — reload local draft.
  useEffect(() => {
    const incoming = typeof field.value === "string" ? field.value : "";
    if (incoming === lastSerializedRef.current) return;
    lastSerializedRef.current = undefined;
    setDraft(parseStored(field.value));
  }, [field.value]);

  // When the countries map loads, re-resolve labels → ISO on the draft.
  useEffect(() => {
    if (!countriesMap || Object.keys(countriesMap).length === 0) return;
    setDraft(prev => normalizeCountryInAddress(countriesMap, prev));
  }, [countriesMap]);

  // Debounce: typing updates local state only; JSON + RHF updates run less often (fewer full-form re-renders).
  useEffect(() => {
    if (readOnly) return;
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = undefined;
      const normalized = normalizeCountryInAddress(map, draft);
      if (normalized.country !== draft.country) {
        setDraft(normalized);
        return;
      }
      pushToForm(normalized);
    }, FLUSH_MS);
    return () => clearTimeout(debounceTimerRef.current);
  }, [draft, map, pushToForm, readOnly]);

  const flushNow = useCallback(() => {
    if (readOnly) return;
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = undefined;
    const normalized = normalizeCountryInAddress(map, draftRef.current);
    setDraft(normalized);
    pushToForm(normalized);
  }, [map, pushToForm, readOnly]);

  const commit = useCallback(
    (patch: Partial<SignUpAddressValue>) => {
      if (readOnly) return;
      setDraft(prev => ({ ...prev, ...patch }));
    },
    [readOnly]
  );

  const onPickCountry = useCallback(
    (isoCodeOrEmpty: string) => {
      if (readOnly) return;
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
      const next = { ...draftRef.current, country: isoCodeOrEmpty };
      const normalized = normalizeCountryInAddress(map, next);
      setDraft(normalized);
      const s = serialize(required, normalized);
      lastSerializedRef.current = s;
      field.onChange(s);
    },
    [field, map, readOnly, required]
  );

  const display = normalizeCountryInAddress(map, draft);

  return (
    <FormItem>
      <FormLabel>
        {label}
        {required ? " *" : ""}
      </FormLabel>
      <div className="space-y-3">
        <FormControl>
          <Input
            autoComplete="address-line1"
            placeholder="Address line 1"
            value={display.line1 ?? ""}
            disabled={readOnly}
            onChange={e => commit({ line1: e.target.value })}
            onBlur={flushNow}
          />
        </FormControl>
        <FormControl>
          <Input
            autoComplete="address-line2"
            placeholder="Address line 2 (optional)"
            value={display.line2 ?? ""}
            disabled={readOnly}
            onChange={e => commit({ line2: e.target.value })}
            onBlur={flushNow}
          />
        </FormControl>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormControl>
            <Input
              autoComplete="address-level2"
              placeholder="City"
              value={display.city ?? ""}
              disabled={readOnly}
              onChange={e => commit({ city: e.target.value })}
              onBlur={flushNow}
            />
          </FormControl>
          <FormControl>
            <Input
              autoComplete="address-level1"
              placeholder="State / province"
              value={display.state ?? ""}
              disabled={readOnly}
              onChange={e => commit({ state: e.target.value })}
              onBlur={flushNow}
            />
          </FormControl>
        </div>
        <FormControl>
          <Input
            autoComplete="postal-code"
            placeholder="Postal code"
            value={display.postalCode ?? ""}
            disabled={readOnly}
            onChange={e => commit({ postalCode: e.target.value })}
            onBlur={flushNow}
          />
        </FormControl>
        <FormControl>
          <SignUpCountrySelect
            required={required}
            valueCode={display.country ?? ""}
            onPick={onPickCountry}
            ariaLabel="Country"
            options={countryOptions}
            optionsLoading={isPending}
            disabled={readOnly}
          />
        </FormControl>
      </div>
      <FormMessage />
    </FormItem>
  );
};
